import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

/**
 * Utility function to merge Tailwind CSS classes
 * Combines clsx and tailwind-merge for optimal class handling
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Generate page numbers with smart ellipsis for pagination.
 * Ported from console-v2 DataTablePagination pattern.
 *
 * Examples:
 *  - totalPages ≤ 5: [1,2,3,4,5]
 *  - currentPage near start: [1,2,3,4,'...',N]
 *  - currentPage in middle: [1,'...',cur-1,cur,cur+1,'...',N]
 *  - currentPage near end: [1,'...',N-3,N-2,N-1,N]
 */
export function getPageNumbers(
  currentPage: number,
  totalPages: number,
): (number | "...")[] {
  const maxVisiblePages = 5;
  const rangeWithDots: (number | "...")[] = [];

  if (totalPages <= maxVisiblePages) {
    for (let i = 1; i <= totalPages; i++) {
      rangeWithDots.push(i);
    }
  } else {
    rangeWithDots.push(1);

    if (currentPage <= 3) {
      for (let i = 2; i <= 4; i++) {
        rangeWithDots.push(i);
      }
      rangeWithDots.push("...", totalPages);
    } else if (currentPage >= totalPages - 2) {
      rangeWithDots.push("...");
      for (let i = totalPages - 3; i <= totalPages; i++) {
        rangeWithDots.push(i);
      }
    } else {
      rangeWithDots.push("...");
      for (let i = currentPage - 1; i <= currentPage + 1; i++) {
        rangeWithDots.push(i);
      }
      rangeWithDots.push("...", totalPages);
    }
  }

  return rangeWithDots;
}

